import React, { useRef } from "react";
import { Button } from "@/components/ui/button";
import { ResponsivePopover } from "@/components/ui/responsive-popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { BaseTemplate } from "@/components/shared/UniversalEntityPage/types";
import type { TemplateType } from "../utils/getTemplates";

export interface TemplateSelectUIProps {
  templates: BaseTemplate[];
  filteredTemplates: BaseTemplate[];
  selectedTemplate: BaseTemplate | null;
  currentValue: string;
  templateType: TemplateType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  searchValue: string;
  onSearchValueChange: (value: string) => void;
  placeholder: string;
  searchPlaceholder: string;
  showClearOption: boolean;
  onTemplateChange: (value: string) => void;
  expandInline?: boolean;
}

export function TemplateSelectUI({
  templates,
  filteredTemplates,
  selectedTemplate,
  currentValue,
  templateType,
  open,
  onOpenChange,
  searchValue,
  onSearchValueChange,
  placeholder,
  searchPlaceholder,
  showClearOption,
  onTemplateChange,
  expandInline = false,
}: TemplateSelectUIProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const selectedOption = templates.find((option) => option.id === currentValue);

  const commandContent = (
    <Command>
      <CommandInput
        placeholder={searchPlaceholder}
        value={searchValue}
        onValueChange={onSearchValueChange}
      />
      <CommandList className="max-h-96">
        <CommandEmpty>No templates found.</CommandEmpty>
        <CommandGroup>
          {showClearOption && (
            <CommandItem
              value="none"
              onSelect={() => {
                onTemplateChange("none");
              }}
              className="cursor-pointer py-3"
            >
              <Check
                className={cn(
                  "mr-2 h-4 w-4",
                  currentValue === "none" ? "opacity-100" : "opacity-0"
                )}
              />
              <div className="flex-1 min-w-0">
                <div className="font-medium">Start from scratch</div>
                <div className="text-xs text-muted-foreground">Create without a template</div>
              </div>
            </CommandItem>
          )}
          {filteredTemplates.map((option) => (
            <CommandItem
              key={option.id}
              value={option.id}
              onSelect={() => {
                onTemplateChange(option.id);
              }}
              className="cursor-pointer py-3 flex-col items-start"
            >
              <div className="flex w-full items-start gap-2">
                <Check
                  className={cn(
                    "mr-2 h-4 w-4 mt-0.5 flex-shrink-0",
                    currentValue === option.id ? "opacity-100" : "opacity-0"
                  )}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="font-semibold text-sm">{option.name}</div>
                    {option.category && (
                      <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded dark:bg-blue-900/40 dark:text-blue-300">
                        {option.category}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                    {option.description}
                  </p>
                  {option.tags && option.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {option.tags.slice(0, 4).map((tag: string) => (
                        <span
                          key={tag}
                          className="text-xs px-2 py-0.5 bg-card-100 text-gray-700 dark:text-gray-300 rounded"
                        >
                          {tag}
                        </span>
                      ))}
                      {option.tags.length > 4 && (
                        <span className="text-xs px-2 py-0.5 text-gray-600 dark:text-gray-400">
                          +{option.tags.length - 4} more
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-3 text-xs text-gray-600 dark:text-gray-400">
                    {templateType === "ticket" && option.estimated_hours && (
                      <span>⏱️ {option.estimated_hours}h est.</span>
                    )}
                    {templateType === "ticket" && option.sla_hours && (
                      <span>🎯 {option.sla_hours}h SLA</span>
                    )}
                    {templateType === "task" && option.priority && (
                      <span>📌 {option.priority}</span>
                    )}
                    {templateType === "task" && option.status && (
                      <span>📊 {option.status}</span>
                    )}
                  </div>
                </div>
              </div>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );

  if (expandInline) {
    return (
      <div className="flex-1 min-w-0 w-full space-y-2">
        {open ? (
          <div
            className="rounded-md border bg-popover p-2 shadow-md"
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            tabIndex={-1}
          >
            <div className="flex items-center justify-end mb-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => onOpenChange(false)}
                aria-label="Close" title="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            {commandContent}
          </div>
        ) : (
          <Button
            ref={triggerRef}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
            onClick={() => onOpenChange(true)}
          >
            <span className="truncate">{selectedOption ? selectedOption.name : placeholder}</span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex-1 min-w-0 w-full">
      <ResponsivePopover
        open={open}
        onOpenChange={onOpenChange}
        content={commandContent}
        title="Select Template"
        className="w-full p-0"
        align="start"
      >
        <Button
          ref={triggerRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          <span className="truncate">{selectedOption ? selectedOption.name : placeholder}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </ResponsivePopover>
    </div>
  );
}
